import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, ArrowRight, Lightning, Globe, CircleNotch, MagicWand } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SEOGenerator() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [formData, setFormData] = useState({
    activity: '',
    city: '',
    objective: 'clients',
  });
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState({});

  // Extraire les informations depuis un site web
  const handleExtractWebsite = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Entrez l\'URL de votre site web');
      return;
    }
    
    setExtracting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/seo/extract-website`,
        { url: websiteUrl },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setFormData({
          ...formData,
          activity: response.data.business_type || '',
          city: response.data.location || '',
        });
        toast.success(`Informations extraites de ${response.data.domain} !`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d\'extraire les informations');
    } finally {
      setExtracting(false);
    }
  };

  const handleGenerateIdeas = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/seo/generate-ideas`,
        formData,
        { withCredentials: true }
      );
      setIdeas(response.data.ideas);
      toast.success(`${response.data.count} pages SEO générées !`);
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePage = async (title, index) => {
    setGenerating({ ...generating, [index]: true });

    try {
      await axios.post(
        `${BACKEND_URL}/api/seo/generate-page`,
        { title, ...formData },
        { 
          withCredentials: true,
          timeout: 120000 // 2 minutes timeout pour la génération IA
        }
      );
      toast.success('Page créée ! Consultez vos pages SEO');
      // Remove from list
      setIdeas(ideas.filter((_, i) => i !== index));
    } catch (error) {
      console.error('SEO page creation error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('La génération prend trop de temps. Réessayez ou essayez une autre page.');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la création. Réessayez dans quelques instants.');
      }
    } finally {
      setGenerating({ ...generating, [index]: false });
    }
  };

  const handleGenerateAll = async () => {
    toast.loading('Génération de toutes les pages... (cela peut prendre 2-3 minutes)');
    
    for (let i = 0; i < Math.min(ideas.length, 10); i++) {
      await handleGeneratePage(ideas[i].title, i);
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    toast.success('Pages générées ! Consultez vos pages SEO');
    navigate('/seo-pages');
  };

  return (
    <AppLayout>
      <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="seo-generator-page">
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Générer des clients
            <span className="block gradient-text">avec Google</span>
          </h1>
          <p className={`text-base sm:text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            Créez 30 pages SEO optimisées qui attirent des visiteurs et des clients
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className={`rounded-2xl p-5 sm:p-6 sticky top-8 ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
              <h2 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Lightning size={24} weight="duotone" className="text-violet-500" />
                Informations
              </h2>
              
              {/* Import depuis site web */}
              <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Globe size={18} className="text-violet-500" weight="duotone" />
                  <span className={`text-sm font-semibold ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                    Import automatique
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://votresite.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className={`flex-1 text-sm ${isDark ? 'bg-white/5 border-white/10' : ''}`}
                    data-testid="website-url-input"
                  />
                  <Button
                    type="button"
                    onClick={handleExtractWebsite}
                    disabled={extracting}
                    className="btn-premium px-3"
                    data-testid="extract-website-btn"
                  >
                    {extracting ? (
                      <CircleNotch size={18} className="animate-spin" />
                    ) : (
                      <MagicWand size={18} />
                    )}
                  </Button>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  Entrez l'URL de votre site pour pré-remplir automatiquement
                </p>
              </div>
              
              <form onSubmit={handleGenerateIdeas} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="activity" className={isDark ? 'text-white/70' : ''}>Votre activité</Label>
                  <Input
                    id="activity"
                    placeholder="Ex: VTC, Plombier, Pet sitter..."
                    value={formData.activity}
                    onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                    required
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="seo-activity-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city" className={isDark ? 'text-white/70' : ''}>Ville / Zone</Label>
                  <Input
                    id="city"
                    placeholder="Ex: Paris, Lyon, Bordeaux..."
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                    data-testid="seo-city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="objective">Objectif</Label>
                  <select
                    id="objective"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors"
                    value={formData.objective}
                    onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  >
                    <option value="clients">Obtenir des clients</option>
                    <option value="appels">Recevoir des appels</option>
                    <option value="visibilite">Augmenter la visibilité</option>
                  </select>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="generate-seo-ideas-btn"
                >
                  {loading ? (
                    <span>Génération en cours...</span>
                  ) : (
                    <>
                      <Sparkle className="mr-2" size={20} weight="duotone" />
                      Générer 30 pages SEO
                    </>
                  )}
                </Button>
              </form>

              {ideas.length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <Button
                    onClick={handleGenerateAll}
                    variant="outline"
                    className="w-full"
                    data-testid="generate-all-pages-btn"
                  >
                    Générer tout (10 premières)
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Génère le contenu des 10 premières pages
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-4"
          >
            {ideas.length === 0 && !loading && (
              <div className="glass rounded-xl p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
                <Sparkle size={48} weight="duotone" className="text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Prêt à attirer des clients</h3>
                <p className="text-muted-foreground max-w-md">
                  Remplissez le formulaire et générez 30 pages SEO optimisées pour Google
                </p>
              </div>
            )}

            {ideas.map((idea, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="glass rounded-xl p-6 flex items-center justify-between gap-4"
              >
                <div className="flex-1">
                  <p className="font-medium">{idea.title}</p>
                </div>
                <Button
                  onClick={() => handleGeneratePage(idea.title, index)}
                  disabled={generating[index]}
                  data-testid={`generate-page-${index}`}
                >
                  {generating[index] ? 'Génération...' : 'Créer cette page'}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </AppLayout>
  );
}
